import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";

// Editor voor de JSONB-kolom "toedieningsvormen" van een werkzame stof.
// Iedere vorm: { naam, werking, bijwerkingen_vaak, bijwerkingen_ernstig, algemene_waarschuwingen }
const INPUT_CLS =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0a5a3c]";

const SUGGESTIES = ["oraal", "injectie", "oogpreparaat", "zetpil", "transdermaal verband"];

const VELDEN = [
  ["werking", "Werking"],
  ["bijwerkingen_vaak", "Vaak voorkomende bijwerkingen"],
  ["bijwerkingen_ernstig", "Ernstige bijwerkingen"],
  ["algemene_waarschuwingen", "Algemene waarschuwingen"],
];

const TvormenEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const [openIdx, setOpenIdx] = useState(null);

  const update = (i, patch) =>
    onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));

  const add = () => {
    onChange([...items, { naam: "", werking: "", bijwerkingen_vaak: "", bijwerkingen_ernstig: "", algemene_waarschuwingen: "" }]);
    setOpenIdx(items.length); // nieuwe vorm meteen openklappen
  };

  const remove = (i) => {
    if (!window.confirm(`Toedieningsvorm "${items[i].naam || "naamloos"}" verwijderen?`)) return;
    onChange(items.filter((_, j) => j !== i));
    setOpenIdx(null);
  };

  return (
    <div className="sm:col-span-2" data-testid="tvormen-editor">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">Toedieningsvormen</span>
        <span className="text-[11px] text-slate-400">{items.length} vorm{items.length === 1 ? "" : "en"}</span>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="overflow-hidden rounded-lg border border-slate-200" data-testid={`tvorm-${i}`}>
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-2">
              <button type="button" onClick={() => setOpenIdx(openIdx === i ? null : i)}
                data-testid={`tvorm-toggle-${i}`} aria-label="In-/uitklappen"
                className="text-slate-500 hover:text-[#0a2540]">
                {openIdx === i ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <input
                value={it.naam || ""}
                list="tvorm-suggesties"
                placeholder="bv. oraal, zetpil…"
                onChange={(e) => update(i, { naam: e.target.value })}
                data-testid={`tvorm-naam-${i}`}
                className="flex-1 bg-transparent text-sm font-medium text-[#0a2540] outline-none placeholder:font-normal placeholder:text-slate-400"
              />
              <button type="button" onClick={() => remove(i)} data-testid={`tvorm-delete-${i}`}
                title="Verwijderen" className="text-rose-500 hover:text-rose-600">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {openIdx === i && (
              <div className="space-y-3 border-t border-slate-100 p-3" data-testid={`tvorm-body-${i}`}>
                {VELDEN.map(([k, label]) => (
                  <div key={k}>
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</label>
                    <textarea rows={2} value={it[k] || ""} onChange={(e) => update(i, { [k]: e.target.value })}
                      data-testid={`tvorm-${k}-${i}`} className={INPUT_CLS} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <p className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-center text-xs text-slate-400">
            Nog geen toedieningsvormen — voeg er één toe.
          </p>
        )}
      </div>
      <datalist id="tvorm-suggesties">
        {SUGGESTIES.map((s) => <option key={s} value={s} />)}
      </datalist>
      <button type="button" onClick={add} data-testid="tvorm-add"
        className="mt-2 flex items-center gap-1.5 rounded-lg border border-[#0a5a3c]/30 px-3 py-1.5 text-xs font-semibold text-[#0a5a3c] hover:bg-[#0a5a3c]/5">
        <Plus className="h-3.5 w-3.5" /> Toedieningsvorm toevoegen
      </button>
    </div>
  );
};

export default TvormenEditor;
